import { computed, ref, watch, type Ref } from 'vue'
import { useCalculateFeesQuery, type CalculateFees } from '@modules/orders'
import { debounce } from '@shared/utils'
import type { InvestQuote } from '../components/InvestOrderForm.vue'
import { investmentValueFor, minTokensFromError, sliderMaxFor } from '../utils/tokenQuantityUtils'

// Pause after the last slider / stepper / keystroke before re-pricing.
const QUOTE_DEBOUNCE_MS = 350

/** What the drawer knows about the offering before the first quote lands. */
export interface OfferingBasics {
  tokenPrice: number
  tokensAvailable: number
  minInvestmentAmount: number
  maxInvestmentAmount: number | null
}

const offeringMinTokens = (basics: OfferingBasics | null): number => {
  const price = basics?.tokenPrice ?? 0
  return price > 0 ? Math.max(Math.ceil((basics?.minInvestmentAmount ?? 0) / price), 1) : 1
}

const offeringMaxTokens = (basics: OfferingBasics | null): number => {
  const price = basics?.tokenPrice ?? 0
  const max = basics?.maxInvestmentAmount
  return max && price > 0 ? Math.floor(max / price) : (basics?.tokensAvailable ?? 0)
}

/**
 * Token count for the invest drawer and its live price from
 * /orders/calculate-fees (mirrors the mobile app):
 * - min: the offering's minimum, raised — never lowered — when a rejected
 *   quote names a higher one;
 * - max: the quote's per-investor `maxTokens`, the offering's until it lands;
 * - sliderMax: max limited by stock, never below min.
 * The last good quote stays on screen while a new quantity is priced.
 */
export const useInvestQuote = (offeringId: Ref<string>, basics: Ref<OfferingBasics | null>) => {
  const tokenCount = ref(1)
  const quotedTokens = ref(0)
  const updateQuotedTokens = debounce((next: number) => {
    quotedTokens.value = next
  }, QUOTE_DEBOUNCE_MS)
  watch(tokenCount, (next) => updateQuotedTokens(next), { immediate: true })

  const feesQuery = useCalculateFeesQuery(
    () => offeringId.value,
    () => quotedTokens.value,
  )

  const lastQuote = ref<CalculateFees | null>(feesQuery.data.value ?? null)
  watch(
    () => feesQuery.data.value,
    (data) => {
      if (data) lastQuote.value = data
    },
  )

  const tokenPrice = computed(() => lastQuote.value?.tokenPrice ?? basics.value?.tokenPrice ?? 0)
  const tokensAvailable = computed(
    () => lastQuote.value?.tokensAvailable ?? basics.value?.tokensAvailable ?? 0,
  )

  // Highest minimum seen so far — the API may raise it; it never drops back.
  const raisedMin = ref(0)
  watch(
    () => feesQuery.error.value,
    (error) => {
      const fromError = minTokensFromError(error?.details, tokenPrice.value)
      if (fromError && fromError > raisedMin.value) raisedMin.value = fromError
    },
  )

  const minTokens = computed(() =>
    Math.max(lastQuote.value?.minTokens || offeringMinTokens(basics.value), raisedMin.value),
  )
  const maxTokens = computed(() =>
    Math.max(lastQuote.value?.maxTokens || offeringMaxTokens(basics.value), minTokens.value),
  )
  const sliderMax = computed(() =>
    sliderMaxFor(minTokens.value, maxTokens.value, tokensAvailable.value),
  )

  const matchingQuote = computed(() =>
    lastQuote.value && lastQuote.value.tokenQuantity === tokenCount.value ? lastQuote.value : null,
  )
  const investmentValue = computed(() =>
    investmentValueFor(tokenCount.value, tokenPrice.value, matchingQuote.value),
  )

  const quote = computed<InvestQuote>(() => ({
    tokenPrice: tokenPrice.value,
    minTokens: minTokens.value,
    maxTokens: maxTokens.value,
    grossAmount: investmentValue.value,
    totalFeeAmount: lastQuote.value?.totalFeeAmount ?? 0,
    netAmount:
      matchingQuote.value?.netAmount ??
      investmentValue.value + (lastQuote.value?.totalFeeAmount ?? 0),
    fees: lastQuote.value?.fees ?? [],
  }))

  // A failed quote is not "still quoting" — without the isError guard a first
  // quote that errors leaves the summary skeletons spinning forever.
  const isQuoting = computed(
    () =>
      feesQuery.isFetching.value ||
      quotedTokens.value !== tokenCount.value ||
      (!lastQuote.value && !feesQuery.isError.value),
  )
  const feesError = computed(() => feesQuery.error.value?.message ?? null)

  // Start at the minimum, and snap up whenever the floor rises past the count.
  watch(
    minTokens,
    (min) => {
      if (tokenCount.value < min) tokenCount.value = min
    },
    { immediate: true },
  )

  const resetToMinimum = () => {
    tokenCount.value = minTokens.value
  }

  return {
    tokenCount,
    quote,
    sliderMax,
    tokensAvailable,
    investmentValue,
    isQuoting,
    feesError,
    resetToMinimum,
  }
}
